import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { ActivePlanState, LegacyPlan } from "../../assets/interfaces/interfaces";
import { CHECKOUT_CLOSED } from "./checkoutUpdate";
import {
  BASE_PROMPTS_PER_DAY,
  BUNDLE_CATALOGUE,
  CatalogueBundle,
  catalogueFromApi,
  formatUsd,
  summarizeSelection,
} from "./bundleCatalogue";

type Step = "intro" | "pick" | "confirm" | "processing" | "done";

export type MigrationFlowProps = {
  legacyPlan: LegacyPlan;
  bundles: CatalogueBundle[];
  basePrompts?: number;
  suggestedIds?: string[];
  // "pick" when the opener already showed the intro (e.g. the expiry nudge,
  // whose screen duplicates it) — otherwise the user sees the intro twice.
  initialStep?: Extract<Step, "intro" | "pick">;
  onConfirm: (bundleIds: string[]) => Promise<unknown>;
  onDone: () => void;
};

export const MigrationFlow = ({
  legacyPlan,
  bundles,
  basePrompts = BASE_PROMPTS_PER_DAY,
  suggestedIds,
  initialStep = "intro",
  onConfirm,
  onDone,
}: MigrationFlowProps) => {
  const purchasable = useMemo(() => bundles.filter(b => b.priceMonthly !== null), [bundles]);
  const suggested = useMemo(
    () => suggestedIds ?? purchasable.map(b => b.id),
    [suggestedIds, purchasable],
  );
  const [step, setStep] = useState<Step>(initialStep);
  const [selectedIds, setSelectedIds] = useState<string[]>(suggested);
  const [error, setError] = useState<string | null>(null);

  const selected = purchasable.filter(b => selectedIds.includes(b.id));
  const summary = summarizeSelection(selected, false, basePrompts);
  const currentPriceLabel = formatUsd(legacyPlan.price_monthly);
  const deadline = dayjs(legacyPlan.migrate_by).format("MMMM D, YYYY");

  const toggle = (id: string) =>
    setSelectedIds(ids => (ids.includes(id) ? ids.filter(i => i !== id) : [...ids, id]));

  const confirmMigration = async () => {
    setStep("processing");
    setError(null);
    try {
      await onConfirm(selectedIds);
      setStep("done");
    }
    catch (e) {
      // closing the checkout page unpaid is a cancel, not a failure
      if ((e as Error)?.message !== CHECKOUT_CLOSED) {
        setError("We couldn't complete the switch. Please try again.");
      }
      setStep("confirm");
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {step === "intro" && (
        <div className="flex flex-col">
          <div className="mb-6">
            <Typography variant="h6/medium-500" className="!mb-2">We&apos;re changing how plans work</Typography>
            <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56">
              Your
              {" "}
              {legacyPlan.type}
              {" "}
              plan (
              {currentPriceLabel}
              /mo) is moving to bundles.
            </Typography>
          </div>
          <div className="rounded-lg bg-gray-50 dark:bg-white/[0.04] px-4 py-3 mb-6">
            <div className="grid grid-cols-2 gap-x-4 text-[11px] tracking-wide uppercase text-mui-black-38 dark:text-mui-white-38 mb-3">
              <span>Now</span>
              <span>With bundles</span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              <span className="text-mui-black-38 dark:text-mui-white-38">Fixed tier pricing</span>
              <span className="font-medium">Pay per bundle</span>
              <span className="text-mui-black-38 dark:text-mui-white-38">All tools included</span>
              <span className="font-medium">Pick what you need</span>
              <span className="text-mui-black-38 dark:text-mui-white-38">Fixed prompt limit</span>
              <span className="font-medium">More bundles, more prompts</span>
            </div>
          </div>
          <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-5">
            Migrate by
            {" "}
            {deadline}
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => setStep("pick")}
            className="!bg-black dark:!bg-white !text-transform-none dark:text-mui-black-87"
          >
            Choose bundles
          </Button>
        </div>
      )}

      {step === "pick" && (
        <div className="flex flex-col gap-4">
          <div>
            <Typography variant="h6/medium-500" className="!mb-1">Pick your bundles</Typography>
            <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56">
              We&apos;ve pre-selected bundles that match your
              {" "}
              {legacyPlan.type}
              {" "}
              usage. Adjust as needed.
            </Typography>
          </div>
          <div className="flex flex-col gap-1">
            {purchasable.map((bundle) => {
              const checked = selectedIds.includes(bundle.id);
              const recommended = suggested.includes(bundle.id);
              return (
                <button
                  type="button"
                  key={bundle.id}
                  onClick={() => toggle(bundle.id)}
                  className={`flex items-center justify-between py-3 px-3 rounded-lg transition-colors text-left ${
                    checked ? "bg-tgpt-secondary-states-selected" : "hover:bg-tgpt-secondary-states-hover"
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Typography variant="body2/regular-400" className="!font-medium">{bundle.name}</Typography>
                      {recommended && (
                        <Typography variant="caption" className="text-tgpt-secondary-main">Recommended</Typography>
                      )}
                    </div>
                    <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                      {bundle.features.length}
                      {" "}
                      tools ·
                      {" "}
                      {formatUsd(bundle.priceMonthly as number)}
                      /mo
                    </Typography>
                  </div>
                  <div
                    data-testid={`migration-check-${bundle.id}`}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${
                      checked ? "bg-tgpt-secondary-main border-tgpt-secondary-main" : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {checked && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" className="text-white">
                        <path d="M2 6L5 9L10 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between pt-2">
            <div>
              <Typography variant="body2/regular-400" className="!font-medium">
                {formatUsd(summary.total)}
                /mo
              </Typography>
              <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                {summary.promptsPerDay}
                {" "}
                prompts/day
              </Typography>
            </div>
            <div className="flex gap-2">
              <Button variant="outlined" color="inherit" onClick={() => setStep("intro")} className="!text-transform-none">
                Back
              </Button>
              <Button
                variant="contained"
                disabled={selectedIds.length === 0}
                onClick={() => setStep("confirm")}
                className="!bg-black dark:!bg-white !text-transform-none dark:text-mui-black-87"
              >
                Review
              </Button>
            </div>
          </div>
        </div>
      )}

      {step === "confirm" && (
        <div className="flex flex-col gap-4">
          <Typography variant="h6/medium-500">Confirm your bundles</Typography>
          <div className="rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-white/[0.04]">
              <Typography variant="caption" component="div" className="text-mui-black-38 dark:text-mui-white-38 uppercase tracking-wide !text-[11px] !mb-1">
                Replacing
              </Typography>
              <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-56">
                {legacyPlan.type}
                {" "}
                ·
                {" "}
                {currentPriceLabel}
                /mo
              </Typography>
            </div>
            <div className="px-4 py-3">
              <Typography variant="caption" component="div" className="text-mui-black-38 dark:text-mui-white-38 uppercase tracking-wide !text-[11px] !mb-2">
                Your bundles
              </Typography>
              <div className="flex flex-col gap-1.5">
                {selected.map(bundle => (
                  <div key={bundle.id} className="flex justify-between">
                    <Typography variant="body2/regular-400">{bundle.name}</Typography>
                    <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-56">
                      {formatUsd(bundle.priceMonthly as number)}
                      /mo
                    </Typography>
                  </div>
                ))}
              </div>
              <div className="flex justify-between pt-2 mt-2 border-t border-gray-100 dark:border-gray-800">
                <Typography variant="caption" className="text-mui-black-60 dark:text-mui-white-56">
                  {summary.promptsPerDay}
                  {" "}
                  prompts/day
                </Typography>
                <Typography variant="body2/regular-400" className="!font-medium">
                  {formatUsd(summary.total)}
                  /mo
                </Typography>
              </div>
            </div>
          </div>
          {error && (
            <Typography variant="caption" component="p" className="text-error-main">{error}</Typography>
          )}
          <div className="flex gap-3">
            <Button variant="outlined" color="inherit" onClick={() => setStep("pick")} className="!text-transform-none flex-1">
              Back
            </Button>
            <Button
              variant="contained"
              onClick={confirmMigration}
              className="!bg-black dark:!bg-white !text-transform-none dark:text-mui-black-87 flex-1"
            >
              Confirm migration
            </Button>
          </div>
        </div>
      )}

      {step === "processing" && (
        <div className="flex flex-col items-center justify-center gap-3 py-12">
          <CircularProgress size={32} sx={{ color: "inherit" }} />
          <Typography variant="body2/regular-400" className="text-mui-black-60 dark:text-mui-white-56">
            Setting up your bundles…
          </Typography>
        </div>
      )}

      {step === "done" && (
        <div className="flex flex-col items-center text-center gap-4 py-4">
          <CheckCircleRoundedIcon className="!w-12 !h-12 text-success-main" />
          <Typography variant="h6/medium-500">You&apos;re all set!</Typography>
          <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56">
            {selected.length}
            {" "}
            bundles active,
            {" "}
            {formatUsd(summary.total)}
            /mo,
            {" "}
            {summary.promptsPerDay}
            {" "}
            prompts a day. You&apos;re good to go.
          </Typography>
          <Button
            variant="contained"
            onClick={onDone}
            className="!bg-black dark:!bg-white !text-transform-none dark:text-mui-black-87"
          >
            Start chatting
          </Button>
        </div>
      )}
    </div>
  );
};

type MigrationModalProps = {
  open: boolean;
  initialStep?: MigrationFlowProps["initialStep"];
  onClose: () => void;
  onConfirm: (bundleIds: string[]) => Promise<unknown>;
};

const MigrationModal = ({ open, initialStep, onClose, onConfirm }: MigrationModalProps) => {
  const legacyPlanLive = useSelector((state: { plan: ActivePlanState }) => state.plan.plan?.legacyPlan);
  const legacyPlanRef = useRef(legacyPlanLive);
  if (legacyPlanLive) legacyPlanRef.current = legacyPlanLive;
  const legacyPlan = legacyPlanLive ?? (open ? legacyPlanRef.current : null);
  const apiPlans = useSelector((state: { plans }) => state.plans.plans);
  const catalogue = useMemo(
    () => catalogueFromApi(apiPlans) ?? { bundles: BUNDLE_CATALOGUE, basePrompts: BASE_PROMPTS_PER_DAY },
    [apiPlans],
  );

  if (!legacyPlan) return null;
  return (
    <Modal open={open} onClose={onClose} aria-labelledby="migration-modal">
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 outline-none">
        <div className="shadow w-modal-mobile sm:w-modal-md bg-white dark:bg-background/paper-elevation-16 rounded-lg p-6 relative text-mui-black-87 dark:text-white">
          <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ position: "absolute", top: 12, right: 12 }}>
            <CloseIcon className="!fill-action-active !w-5 !h-5" />
          </IconButton>
          <MigrationFlow
            legacyPlan={legacyPlan}
            bundles={catalogue.bundles}
            basePrompts={catalogue.basePrompts}
            initialStep={initialStep}
            onConfirm={onConfirm}
            onDone={onClose}
          />
        </div>
      </div>
    </Modal>
  );
};

export default MigrationModal;
